import { Box, Popover, styled, Typography } from "@mui/material";
import { differenceInDays } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Mortgage, OtherMortgage } from "../../../../model/MortgageExtensionModel";
import { DateFormat, formatDate, getDay, getMonth, getYear, timestampToDate } from "../../../../utils/DateUtils";
import { formatAmount } from "../../../../utils/NumberUtils";
import { renderProductType } from "../../../../utils/Utils";

const VARIABLE_MORTGAGE_TYPE = "hypothek_var";

const Svg = styled("svg")(({ theme }) => ({
    fontFamily: "Yoga Sans",
    overflow: "visible !important",
    fontSize: "18px",
    fontWeight: 300,
}));

const getGraphicWidth = () => {
    const calculatedWidth = window.innerWidth - 250;
    const capMax = calculatedWidth > 900 ? 900 : calculatedWidth;
    return capMax < 750 ? 750 : capMax;
};

const MortgageTotalLoadGraphic = (props: { chosenMortgage: Mortgage; newMortgage: Mortgage }) => {
    const { chosenMortgage, newMortgage } = props;

    const { i18n, t } = useTranslation();

    const configuration = {
        width: getGraphicWidth(),
        padding: 20,
        paddingTop: 70,
        paddingBottom: 30,
        minHeightMortgage: 80,
        backgroundNewMortgage: "#E5EBEF",
        backgroundOtherMortgage: "#F5F6F8",
        backgroundRepaymentSign: "#666",
        backgroundExpiringMortgageTextArea: "#fff",
        borderColor: "#9B9B9B",
    };

    const [popoverAnchorEl, setPopoverAnchorEl] = useState<SVGSVGElement | null>(null);

    const handlePopoverOpen = (event: React.MouseEvent<SVGSVGElement>) => {
        setPopoverAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setPopoverAnchorEl(null);
    };

    function renderSvg() {
        // total width and height
        const { totalHeight, height } = calculateHeight(chosenMortgage); // height is calculated dynamically
        const totalWidth = configuration.width;

        // take {allocationWidthRatioForExpiring} of width for expiring mortgage (left part), the rest is allocated to right part (future time)
        // this ratio of 0.34 vs 0.64 is fix
        const allocationWidthRatioForExpiring = 0.34;
        const widthExpiringMortgages = allocationWidthRatioForExpiring * totalWidth;
        const remainingWidthForFuture = totalWidth * (1 - allocationWidthRatioForExpiring);

        const expiringMortgageAmount = chosenMortgage.amount;
        const newMortgageAmount = newMortgage.amount;
        const otherMortgagesAmount = newMortgage.totalSumOtherLiabilities ?? 0;

        const sumExpiringAndOther = expiringMortgageAmount + otherMortgagesAmount;
        const ratioExpiringToOtherMortgages = expiringMortgageAmount / sumExpiringAndOther;

        let heightExpiringMortgage = height * ratioExpiringToOtherMortgages;
        // it could happen that the expiring mortgage is too "small", so adjust for it - see calculateHeight method
        if (heightExpiringMortgage < configuration.minHeightMortgage) {
            heightExpiringMortgage = configuration.minHeightMortgage;
        }

        const ratioNewToExpiringMortgage = newMortgageAmount / expiringMortgageAmount;
        let heightNewMortgage = heightExpiringMortgage * ratioNewToExpiringMortgage;

        // in order for all text to be displayed (UX requirement), we give up on proportionality
        if (heightNewMortgage < configuration.minHeightMortgage) {
            heightNewMortgage = configuration.minHeightMortgage;
        }
        const heightDifferenceExpiringAndNew = heightExpiringMortgage - heightNewMortgage;
        const offsetTopNewMortgage = heightDifferenceExpiringAndNew;

        const textContainerOffset = 10;

        const expirationYearOfExpiringMortgage = getYear(chosenMortgage.expirationDate);
        const expirationMonthOfExpiringMortgage = getMonth(chosenMortgage.expirationDate);
        const expirationDayOfExpiringMortgage = getDay(chosenMortgage.expirationDate);
        const numberOfYearsToDisplay = getNumberOfYearsToDisplay(expirationYearOfExpiringMortgage);
        const widthPerYear = remainingWidthForFuture / numberOfYearsToDisplay;

        const widthOffsetsBasedOnMonth = getOffsetWidthsBasedOnDate(
            widthPerYear,
            expirationMonthOfExpiringMortgage,
            expirationDayOfExpiringMortgage
        );

        sortMortgages();

        const comingYearAfterExpiration = expirationYearOfExpiringMortgage + 1;
        const maxYearInTheFutureToDisplay = comingYearAfterExpiration + numberOfYearsToDisplay;

        // provide standard set of calculated variables for different methods
        const componentContext = {
            widthPerYear: widthPerYear,
            heightExpiringMortgage: heightExpiringMortgage,
            sumExpiringAndOther: sumExpiringAndOther,
            height: height,
            expirationYearOfExpiringMortgage: expirationYearOfExpiringMortgage,
            widthExpiringMortgages: widthExpiringMortgages,
            textContainerOffset: textContainerOffset,
            maxYearInTheFutureToDisplay: maxYearInTheFutureToDisplay,
            comingYearAfterExpiration: comingYearAfterExpiration,
            heightNewMortgage: heightNewMortgage,
            heightDifferenceExpiringAndNew: heightDifferenceExpiringAndNew,
            expiringMortgageAmount: expiringMortgageAmount,
            newMortgageAmount: newMortgageAmount,
            offsetTopNewMortgage: offsetTopNewMortgage,
            widthOffsetsBasedOnMonth: widthOffsetsBasedOnMonth,
            expirationMonthOfExpiringMortgage: expirationMonthOfExpiringMortgage,
            otherMortgagesHeight: undefined,
        };

        const expiringMortgageSvg = drawExpiringMortgage(componentContext);
        const newMortgageSvg = drawNewMortgage(componentContext);
        const { otherMortgagesSvg, otherMortgagesHeight } = drawOtherMortgages(componentContext);
        componentContext.otherMortgagesHeight = otherMortgagesHeight;
        const yearMarkersSvg = drawYears(componentContext);
        const popoverRepayment = drawPopoverRepayment(componentContext);
        const todaySvg = drawTodayLine(componentContext);
        const heightReservedForInfoTextBelowGraphic = 46;

        return (
            <div
                style={{
                    height: totalHeight + heightReservedForInfoTextBelowGraphic,
                    padding: configuration.padding,
                    paddingTop: configuration.paddingTop,
                    paddingBottom: configuration.paddingBottom,
                    maxWidth: "1180px",
                    backgroundColor: "white",
                }}>
                <div
                    className="mortgage-graphic"
                    style={{
                        height: totalHeight,
                        width: totalWidth,
                    }}>
                    <Svg height={totalHeight} width={totalWidth}>
                        <line
                            id="LINE_FOR_EXPIRING_DATE"
                            strokeDasharray="10"
                            x1={widthExpiringMortgages}
                            y1="-20"
                            x2={widthExpiringMortgages}
                            y2="0"
                            stroke="black"
                            strokeWidth="1"
                        />

                        <Svg id="EXPIRATION_DATE" x={widthExpiringMortgages - 40} y={-30}>
                            <text>
                                <tspan>{formatDate(chosenMortgage.expirationDate, DateFormat.DATE)}</tspan>
                            </text>
                        </Svg>

                        {expiringMortgageSvg}
                        {newMortgageSvg}

                        <g id="OTHER_MORTGAGES">{otherMortgagesSvg}</g>

                        <line
                            id="LINE_EXTENSION_ON_SIDES"
                            x1={-20}
                            y1={heightExpiringMortgage + otherMortgagesHeight}
                            x2={totalWidth + 20}
                            y2={heightExpiringMortgage + otherMortgagesHeight}
                            stroke={configuration.borderColor}
                            strokeWidth="1"
                        />

                        <line
                            id="LINE_EXPIRING_MORTGAGE_EXPIRATION"
                            strokeDasharray="10"
                            x1={widthExpiringMortgages}
                            y1={heightExpiringMortgage}
                            x2={widthExpiringMortgages}
                            y2={heightExpiringMortgage + otherMortgagesHeight}
                            stroke="black"
                            strokeWidth="1"
                        />

                        {popoverRepayment}
                        {todaySvg}

                        <g id="YEAR_MARKERS">{yearMarkersSvg}</g>
                        <text id="NOTE_TEXT">
                            <tspan
                                style={{ fontSize: "16px" }}
                                fill={configuration.borderColor}
                                x={-20}
                                dy={heightExpiringMortgage + otherMortgagesHeight + 80}>
                                {t(
                                    "ConfigurationScreen.Schematic representation without direct amortisation and mortgages not yet paid off."
                                )}
                            </tspan>
                        </text>
                    </Svg>
                </div>
            </div>
        );
    }

    /**
     * Returns the longest duration + 1 year of all mortgages.
     * What we have to take into account is how many years remain for the other mortgages, not their entire duration.
     * We need this info, so that we can paint all the future years.
     *
     * Ignores "Variable mortgage" types, since they have their duration of N years hard-coded.
     * This value should not influence the number of years calculation.
     */
    function getNumberOfYearsToDisplay(expirationYearOfExpiringMortgage: number) {
        let longestMortgageInYears = Number(newMortgage?.duration); // hypothesis - new one is the longest one

        if (chosenMortgage?.otherLiabilities) {
            for (const otherMortgage of newMortgage?.otherLiabilities) {
                if (otherMortgage.amount === 0 || isVariableMortgage(otherMortgage)) {
                    continue;
                }
                const expirationYear = getYear(otherMortgage.expirationDate);
                const remainingYearsToRepay = expirationYear - expirationYearOfExpiringMortgage;

                if (remainingYearsToRepay > longestMortgageInYears) {
                    longestMortgageInYears = remainingYearsToRepay;
                }
            }
        }

        // show one more year after the last one, since the expiration is not only at the start of the year (could be middle or end)
        // so we always want to display this + 1 additional year
        longestMortgageInYears += 1;

        return longestMortgageInYears;
    }

    /**
     * Checks if a given mortgage is of "variable" type.
     */
    function isVariableMortgage(mortgage: Mortgage | OtherMortgage) {
        return VARIABLE_MORTGAGE_TYPE === mortgage.productType;
    }

    /**
     * Take into consideration the expiry month relative to the year start and end.
     * This method calculates (approximately) width offset to the left (expirationMonth towards year start) and right (expirationMonth towards end year) relative to the year width.
     * Example for february (expirationMonthIndex = 1) and given width per year of 120:
     * offsetLeft would be 20, offsetRight 100.
     */
    function getOffsetWidthsBasedOnDate(widthPerYear: number, expirationMonthIndex: number, expirationDay: number) {
        const oneMonthWidth = widthPerYear / 12;
        const oneDayWidth = oneMonthWidth / 31; // not perfect, but will do
        const widthOffsetLeft =
            oneMonthWidth * (1 + expirationMonthIndex) - oneMonthWidth + oneDayWidth * expirationDay;
        const widthOffsetRight = widthPerYear - widthOffsetLeft;

        return [widthOffsetLeft, widthOffsetRight];
    }

    /**
     * Sorts mortgages based on the remaining years they have left until they are fully re-payed.
     * The order is: "shortest" ones come first, "longest" ones come last. Variable come also last, since they count as if they have max duration.
     * The order is changed permanently in the model, but this is ok for us.
     * Comparing only duration does not suffice, because some mortgage may have duration of 10 years, but they were started 9 years ago.
     * Then it makes it confusing for the customers, since their context is "now", meaning the current year.
     */
    function sortMortgages() {
        chosenMortgage.otherLiabilities &&
            chosenMortgage.otherLiabilities.sort((firstMortgage, secondMortgage) => {
                if (isVariableMortgage(firstMortgage)) {
                    return 1;
                }
                if (isVariableMortgage(secondMortgage)) {
                    return -1;
                }
                const currentYear = new Date().getFullYear();
                const remainingYearsToRepayFirstMortgage = getYear(firstMortgage.expirationDate) - currentYear;
                const remainingYearsToRepaySecondMortgage = getYear(secondMortgage.expirationDate) - currentYear;

                return remainingYearsToRepayFirstMortgage - remainingYearsToRepaySecondMortgage;
            });
    }

    function drawExpiringMortgage(componentContext: any) {
        const { heightExpiringMortgage, widthExpiringMortgages, textContainerOffset, expiringMortgageAmount } =
            componentContext;

        return (
            <g id="EXPIRING_MORTGAGE">
                <defs>
                    <pattern
                        id="BACKGROUND_PATTERN_EXPIRING_MORTGAGE"
                        patternUnits="userSpaceOnUse"
                        width="9"
                        height="9"
                        patternTransform="rotate(135)">
                        <line x1="0" y="0" x2="0" y2="100" stroke="#D3D3D3" strokeWidth="3" />
                    </pattern>
                </defs>
                <rect
                    id="EXPIRING_MORTGAGE_BLOCK"
                    x={0}
                    y={0}
                    width={widthExpiringMortgages}
                    height={heightExpiringMortgage}
                    stroke={configuration.borderColor}
                    strokeWidth="0.5"
                    fill="url(#BACKGROUND_PATTERN_EXPIRING_MORTGAGE)"
                />
                <rect
                    id="EXPIRING_MORTGAGE_TEXT_WRAPPER"
                    x={textContainerOffset}
                    y={textContainerOffset}
                    height="80"
                    width="250"
                    fill={configuration.backgroundExpiringMortgageTextArea}
                />
                <text id="EXPIRING_MORTGAGE_TEXT" x={textContainerOffset + 10} y={textContainerOffset + 25}>
                    <tspan x={textContainerOffset + 10} dy="0" style={{ fontWeight: 500 }}>
                        {renderProductType(chosenMortgage, i18n, t)}
                    </tspan>
                    <tspan x={textContainerOffset + 10} dy={textContainerOffset + 10}>
                        {t("ConfigurationScreen.(expiring)")}
                    </tspan>
                    <tspan x={textContainerOffset + 10} dy={textContainerOffset + 14}>
                        {newMortgage.currency}&nbsp;
                        {formatAmount(expiringMortgageAmount)}
                    </tspan>
                </text>
            </g>
        );
    }

    function drawNewMortgage(componentContext: any) {
        const {
            widthExpiringMortgages,
            textContainerOffset,
            heightNewMortgage,
            offsetTopNewMortgage,
            widthPerYear,
            newMortgageAmount,
        } = componentContext;
        // here its simple, month of new mortgage is the same as starting point (expiring mortgage), so just add years
        const widthNewMortgage = widthPerYear * Number(newMortgage.duration);

        return (
            <g id="NEW_MORTGAGE">
                <rect
                    id="NEW_MORTGAGE_BLOCK"
                    x={widthExpiringMortgages}
                    y={offsetTopNewMortgage}
                    width={widthNewMortgage}
                    height={heightNewMortgage}
                    stroke="#007DB2"
                    strokeWidth="0.5"
                    fill={configuration.backgroundNewMortgage}
                />
                <text id="NEW_MORTGAGE_TEXT" y={offsetTopNewMortgage + 20}>
                    <tspan x={widthExpiringMortgages} dx={textContainerOffset} dy="10" style={{ fontWeight: 500 }}>
                        {renderProductType(chosenMortgage, i18n, t)}&nbsp;{t("ConfigurationScreen.(new)")}
                    </tspan>
                    <tspan x={widthExpiringMortgages} dx={textContainerOffset} dy={textContainerOffset + 15}>
                        {newMortgage.currency} {formatAmount(newMortgageAmount)}
                    </tspan>
                </text>
            </g>
        );
    }

    function drawOtherMortgages(componentContext: any): any {
        const {
            widthPerYear,
            heightExpiringMortgage,
            sumExpiringAndOther,
            height,
            expirationYearOfExpiringMortgage,
            widthExpiringMortgages,
            textContainerOffset,
            widthOffsetsBasedOnMonth,
            maxYearInTheFutureToDisplay,
        } = componentContext;

        // height of previous mortgage is starting y position of new mortgage
        let otherMortgagesHeight = 0;

        // draw N other mortgages
        const otherMortgagesSvg =
            chosenMortgage.otherLiabilities &&
            chosenMortgage.otherLiabilities
                .filter((otherMortgage) => otherMortgage.amount > 0)
                .map((otherMortgage, index) => {
                    let currentMortgageHeight = (otherMortgage.amount / sumExpiringAndOther) * height;

                    if (currentMortgageHeight < configuration.minHeightMortgage) {
                        currentMortgageHeight = configuration.minHeightMortgage;
                    }

                    // how "tall" is the current graphic
                    const currentTotalHeight = heightExpiringMortgage + otherMortgagesHeight;
                    // add height of current one for the drawing of next other mortgage
                    otherMortgagesHeight += currentMortgageHeight;

                    let expirationYear = getYear(otherMortgage.expirationDate);
                    let expirationMonth = getMonth(otherMortgage.expirationDate);
                    let expirationDay = getDay(otherMortgage.expirationDate);

                    const variableMortgage = isVariableMortgage(otherMortgage);
                    if (variableMortgage) {
                        expirationYear = maxYearInTheFutureToDisplay - 1;
                        expirationMonth = 0; // january - start of the last visible year
                    }
                    const remainingYears = expirationYear - expirationYearOfExpiringMortgage;
                    const monthOffsetsOtherMortgage = getOffsetWidthsBasedOnDate(
                        widthPerYear,
                        expirationMonth,
                        expirationDay
                    );
                    // the expiration month is not the same as for the new mortgage, so we have to calculate width differently
                    // so first reset to start of the year by deducting width that expiring mortgage took already for its months
                    // when we are done with this, we are "at the start of the year"
                    // then just add so much width to adjust for the expiration month of this mortgage
                    const startOfTheYearWidth =
                        widthExpiringMortgages + remainingYears * widthPerYear - widthOffsetsBasedOnMonth[0];
                    const width = startOfTheYearWidth + monthOffsetsOtherMortgage[0];

                    const id = "OTHER_MORTGAGE_" + (index + 1);

                    let fontSize = "18px";
                    let offsetTopText = 15;
                    let offsetLeftAmount = 10;
                    let offsetTopAmount = 15;

                    // if currency is provided for this mortgage - take it, if not fallback to the currency of "new mortgage"
                    const currency = otherMortgage.currency ? otherMortgage.currency : newMortgage.currency;

                    return (
                        <g id={id} key={index}>
                            <rect
                                id="OTHER_MORTGAGES_BLOCK"
                                x={0}
                                y={currentTotalHeight}
                                width={width}
                                className="other-mortgages"
                                height={currentMortgageHeight}
                                stroke={configuration.borderColor}
                                strokeWidth="0.5"
                                fill={configuration.backgroundOtherMortgage}
                            />
                            <text
                                id="OTHER_MORTGAGES_TEXT"
                                x={0}
                                y={currentTotalHeight + 20}
                                style={{ fontSize: fontSize }}>
                                <tspan x="10" dy={offsetTopText} style={{ fontWeight: 500 }}>
                                    {renderProductType(chosenMortgage, i18n, t)}
                                </tspan>
                                <tspan x="10" dy={offsetTopText + 5} style={{ fontWeight: 500 }}>
                                    {t("ConfigurationScreen.(existing)")}
                                </tspan>
                                <tspan x={offsetLeftAmount} dy={textContainerOffset + offsetTopAmount}>
                                    {currency}&nbsp;
                                    {formatAmount(otherMortgage.amount)}
                                </tspan>
                            </text>

                            {variableMortgage && (
                                <line
                                    id="LINE_EXTENSION"
                                    strokeDasharray="5"
                                    stroke="black"
                                    x1={width}
                                    y1={currentTotalHeight + currentMortgageHeight}
                                    x2={width}
                                    y2={currentTotalHeight}
                                />
                            )}
                        </g>
                    );
                });

        return { otherMortgagesSvg, otherMortgagesHeight };
    }

    function drawYears(componentContext: any) {
        const {
            widthPerYear,
            heightExpiringMortgage,
            widthExpiringMortgages,
            comingYearAfterExpiration,
            maxYearInTheFutureToDisplay,
            otherMortgagesHeight,
            widthOffsetsBasedOnMonth,
        } = componentContext;

        const years = [];
        for (let year = comingYearAfterExpiration; year < maxYearInTheFutureToDisplay; year++) {
            years.push(year);
        }

        // draw year markers
        const yearMarkersSvg = years.map((year, index) => {
            const id = "YEAR_MARKER_" + year;
            const distanceLeft = widthOffsetsBasedOnMonth[1] + widthExpiringMortgages + widthPerYear * index;

            return (
                <g id={id} key={id}>
                    <line
                        id="LINE_FOR_YEAR"
                        x1={distanceLeft}
                        y1={heightExpiringMortgage + otherMortgagesHeight}
                        x2={distanceLeft}
                        y2={heightExpiringMortgage + otherMortgagesHeight + 15}
                        stroke="black"
                        strokeWidth="1"
                    />

                    <Svg id="YEAR_LABEL" x={distanceLeft - 15} y={heightExpiringMortgage + otherMortgagesHeight + 40}>
                        <text>
                            <tspan className="year-text" style={{ fontSize: "16px" }}>
                                {year}
                            </tspan>
                        </text>
                    </Svg>
                </g>
            );
        });

        return yearMarkersSvg;
    }

    function drawPopoverRepayment(componentContext: any) {
        const { widthExpiringMortgages, heightDifferenceExpiringAndNew, expiringMortgageAmount, newMortgageAmount } =
            componentContext;

        // to vertically center repayment sign in the space where the heights are different
        const offSetTopRepayment = (heightDifferenceExpiringAndNew - 20) / 2;
        const repaymentAmount = expiringMortgageAmount - newMortgageAmount;

        return (
            <>
                <Popover
                    id="popover-repayment"
                    className="lukb-tooltip-text"
                    open={Boolean(popoverAnchorEl)}
                    sx={{
                        pointerEvents: "none",
                        marginLeft: "16px",
                    }}
                    anchorOrigin={{
                        vertical: "center",
                        horizontal: "center",
                    }}
                    transformOrigin={{
                        vertical: "center",
                        horizontal: "left",
                    }}
                    anchorEl={popoverAnchorEl}
                    elevation={4}
                    disableRestoreFocus>
                    <Box sx={{ padding: "8px", textAlign: "center", color: "#333333" }}>
                        <Typography variant={"body2"}>{t("General.Repayment")}</Typography>
                        <Typography variant={"body2"}>
                            {newMortgage.currency} {formatAmount(repaymentAmount)}
                        </Typography>
                    </Box>
                </Popover>
                <Svg
                    onMouseEnter={handlePopoverOpen}
                    onMouseLeave={handlePopoverClose}
                    id="REPAYMENT_SIGN"
                    style={{
                        visibility: repaymentAmount > 0 ? "visible" : "hidden",
                        width: "30rem",
                        backgroundColor: "#fff",
                        textAlign: "center",
                        borderRadius: "6px",
                        cursor: "pointer",
                    }}
                    className="repayment-sign"
                    x={widthExpiringMortgages}
                    y={offSetTopRepayment}>
                    <rect
                        id="REPAYMENT_SIGN"
                        transform="rotate(45)"
                        height="15"
                        width="15"
                        fill={configuration.backgroundRepaymentSign}
                    />
                </Svg>
            </>
        );
    }

    /**
     * Draws a marker for the TODAY date.
     */
    function drawTodayLine(componentContext: any) {
        const { heightExpiringMortgage, widthExpiringMortgages, otherMortgagesHeight, widthPerYear } = componentContext;
        const widthPerDay = widthPerYear / 365;

        const currentDate = new Date();
        const expirationDate = timestampToDate(chosenMortgage.expirationDate);
        const diffInDays = differenceInDays(currentDate, expirationDate) * -1;
        let widthToMoveAwayFromExpiringMortgage = widthPerDay * diffInDays;
        let centerTextOffset = 25;
        let xPosition = 0;

        if (widthToMoveAwayFromExpiringMortgage <= widthExpiringMortgages) {
            xPosition = widthExpiringMortgages - widthToMoveAwayFromExpiringMortgage;
            centerTextOffset = centerTextOffset + 18;
        } else {
            // do not allow the marker to leave the graphic area if too distant in the past
            xPosition = 10;
        }

        return (
            <g id="TODAY_MARKER">
                <Svg
                    id="TODAY_TEXT"
                    x={xPosition - centerTextOffset}
                    y={heightExpiringMortgage + otherMortgagesHeight + 40}>
                    <text>
                        <tspan style={{ fontWeight: 500 }}>{t("General.today")}</tspan>
                    </text>
                </Svg>

                <line
                    id="LINE_FOR_TODAY"
                    x1={xPosition}
                    y1={heightExpiringMortgage + otherMortgagesHeight - 15}
                    x2={xPosition}
                    y2={heightExpiringMortgage + otherMortgagesHeight + 15}
                    stroke="black"
                    strokeWidth="1"
                />
            </g>
        );
    }

    /**
     * Calculates total height and height.
     * These are based on all mortgages and their amounts.
     * Total height is the real height taken (in pixels), and height is totalHeight minus height reserved for "small mortgages".
     * They are special case, since some amounts may be too small relative to the others to be visible so they have to have some minimum height.
     * Total height is used to draw the whole container area (outer area), and height is used for the graphic itself (inner area).
     *
     * @param mortgage
     */
    function calculateHeight(chosenMortgage: Mortgage) {
        let baseHeight = 400; // default, when there are no other liabilities

        let totalHeight = 0;
        let height = 0;

        const otherApplicableLiabilities = chosenMortgage.otherLiabilities?.filter(
            (otherMortgage) => otherMortgage.amount > 0
        );

        if (!otherApplicableLiabilities?.length) {
            totalHeight = baseHeight;
            height = baseHeight;
            return { totalHeight, height };
        }

        if (otherApplicableLiabilities.length > 0) {
            baseHeight = 250;

            // merge expiring and other mortgages together
            const allMortgages: Array<Mortgage | OtherMortgage> = Array.from(otherApplicableLiabilities);
            allMortgages.push(chosenMortgage);

            // get mortgage with max amount to use as base
            let mortgageWithMaxAmount: Mortgage | OtherMortgage = chosenMortgage;
            for (let mortgage of allMortgages) {
                if (mortgage.amount > mortgageWithMaxAmount.amount) {
                    mortgageWithMaxAmount = mortgage;
                }
            }

            // how "tall" is each unit of currency, in relation to mortgage with max amount and base height
            const oneCurrencyUnitHeightInPixels = baseHeight / mortgageWithMaxAmount.amount;

            for (let mortgage of allMortgages) {
                let currentHeight = oneCurrencyUnitHeightInPixels * mortgage.amount;
                let currentRealHeight = currentHeight;

                if (currentHeight < configuration.minHeightMortgage) {
                    currentRealHeight = configuration.minHeightMortgage;
                    // these will be adjusted later when drawn
                    currentHeight = 0;
                }
                totalHeight += currentRealHeight;
                height += currentHeight;
            }
        }

        return { totalHeight, height };
    }

    return <>{renderSvg()}</>;
};

export default MortgageTotalLoadGraphic;
